import { createServerFn } from "@tanstack/react-start";

/**
 * Server-only Loops.so integration for the schedule lead forms.
 *
 * The API key is read from `process.env.LOOPS_API_KEY` inside the handler
 * (Cloudflare Workers inject env at request time). Set it in the Cloudflare
 * dashboard for production and in `.dev.vars` for local dev.
 */

const LOOPS_UPDATE_URL = "https://app.loops.so/api/v1/contacts/update";

/** Reads the Loops API key from the runtime environment. */
function getLoopsApiKey(): string | undefined {
  const fromProcess = process.env.LOOPS_API_KEY;
  return fromProcess ? fromProcess : undefined;
}

/** Fields the form collects across its steps. All optional except `email`. */
export type LoopsContactInput = {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  companyWebsite?: string;
  /** Which landing-page form the lead came from (e.g. "schedule-agency"). */
  formName?: string;
  /** Qualifying answers, keyed by field name — Loops auto-creates each property. */
  properties?: Record<string, string>;
};

type LoopsResult = { ok: boolean; message?: string };

/**
 * Last-resort recovery log: when the Loops upsert cannot complete, the lead
 * would otherwise be lost forever (the payload exists nowhere else — analytics
 * deliberately strips it). Logged as a single JSON line under a stable marker
 * so it can be found in Workers Logs / `wrangler tail` and replayed by hand.
 */
function logUnsavedLead(reason: string, payload: Record<string, string>): void {
  console.error(`[unsaved-lead] reason=${reason} payload=${JSON.stringify(payload)}`);
}

/**
 * Upserts a Loops contact with whatever fields the current step provides.
 * Fails soft: a missing key or a Loops error returns `{ ok: false }` rather than
 * throwing, so the booking flow is never hard-blocked by lead capture.
 */
export const upsertLoopsContact = createServerFn({ method: "POST" })
  .inputValidator((data: LoopsContactInput) => data)
  .handler(async ({ data }): Promise<LoopsResult> => {
    const email = data.email?.trim();
    if (!email) {
      return { ok: false, message: "Email is required." };
    }

    // Only send properties that have a value; Loops auto-creates custom props.
    const payload: Record<string, string> = { email };
    payload.source = data.formName ? `lp-${data.formName}` : "lp-schedule";
    if (data.formName) payload.formName = data.formName;
    if (data.firstName) payload.firstName = data.firstName;
    if (data.lastName) payload.lastName = data.lastName;
    if (data.phone) payload.phone = data.phone;
    if (data.companyWebsite) payload.companyWebsite = data.companyWebsite;
    if (data.properties) {
      for (const [key, value] of Object.entries(data.properties)) {
        if (value) payload[key] = value;
      }
    }

    const apiKey = getLoopsApiKey();
    if (!apiKey) {
      logUnsavedLead("missing-api-key", payload);
      return { ok: false, message: "Lead capture is not configured." };
    }

    try {
      const res = await fetch(LOOPS_UPDATE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) return { ok: true };

      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      console.error("Loops contact upsert failed:", res.status, body?.message);
      logUnsavedLead(`loops-${res.status}`, payload);
      return { ok: false, message: body?.message ?? "Could not save your details." };
    } catch (err) {
      console.error("Loops contact upsert threw:", err);
      logUnsavedLead("fetch-error", payload);
      return { ok: false, message: "Could not save your details." };
    }
  });
